/* eslint-disable react-hooks/exhaustive-deps -- need to specify when to run the effect */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren
} from "react";
import { createStore, useStore, type StoreApi } from "zustand";

import type { Category, Allowance } from "~/api/products/types";
import type { UserLimitClient } from "~/utils/user-limits/types";
import { type PosProduct } from "~/types/product";

import type { PosProductInBasket } from "./types";
import type { BasketStore } from "./store.types";
import { usePosActiveCategory } from "./hooks/use-pos-categories";
import { useBasket, useIsBasketEmpty } from "./hooks/use-basket";
import { useBasketProducts } from "./hooks/use-basket-products";
import { buildBasketSum } from "./build-basket-sum";

type BasketContextState = {
  activeCategory: Category | null;
  isBasketEmpty: boolean;

  // TO-DO: [POS-2371] cleanup, most likely we don't need all of them
  productsStatus: "success" | "error" | "pending";
  basket: BasketStore["products"];
  products: PosProduct[];
  productsInBasket: PosProductInBasket[];
  defaultTaxData: {
    taxRate: number;
    taxRateName: string | null;
    taxRateId: string | null;
  };
  allowances: Allowance[];
  userLimits: UserLimitClient[];
  sum: {
    unitPriceSubTotalInclVat: number; // without discounts
    unitPriceTotalInclVat: number;
    /** Deposits across the basket, already inside `unitPriceTotalInclVat`. */
    depositTotalInclVat: number;
    depositTotalExclVat: number;
    unitPriceTotalExclVat: number;
    orderDiscountTotalInclVat: number;
    orderDiscountableTotalInclVat: number;
  };
};

const INITIAL_STATE: BasketContextState = {
  activeCategory: null,
  isBasketEmpty: true,
  productsStatus: "pending",
  basket: [],
  products: [],
  productsInBasket: [],
  defaultTaxData: {
    taxRate: 0,
    taxRateName: null,
    taxRateId: null
  },
  sum: {
    unitPriceSubTotalInclVat: 0,
    unitPriceTotalInclVat: 0,
    depositTotalInclVat: 0,
    depositTotalExclVat: 0,
    unitPriceTotalExclVat: 0,
    orderDiscountTotalInclVat: 0,
    orderDiscountableTotalInclVat: 0
  },
  userLimits: [],
  allowances: []
};

export const BasketContext = createContext<StoreApi<BasketContextState> | null>(
  null
);

export const BasketProvider = ({ children }: PropsWithChildren) => {
  // eslint-disable-next-line react/hook-use-state -- useState for one time initialization (https://tkdodo.eu/blog/use-state-for-one-time-initializations)
  const [store] = useState(() => createStore(() => INITIAL_STATE));

  const activeCategory = usePosActiveCategory();
  const isBasketEmpty = useIsBasketEmpty();
  const basket = useBasket();

  const {
    products,
    productsInBasket,
    defaultTaxData,
    productsStatus,
    totalUnit,
    totalUnitDiscounted,
    totalUnitExclVatDiscounted,
    totalDeposit,
    totalDepositExclVat,
    totalOrderDiscount,
    totalOrderDiscountable,
    allowances,
    userLimits
  } = useBasketProducts();

  useEffect(() => {
    store.setState({ activeCategory });
  }, [activeCategory]);

  useEffect(() => {
    store.setState({ isBasketEmpty });
  }, [isBasketEmpty]);

  useEffect(() => {
    store.setState({ products });
  }, [products]);

  useEffect(() => {
    store.setState({ productsInBasket });
  }, [productsInBasket]);

  useEffect(() => {
    store.setState({ defaultTaxData });
  }, [defaultTaxData]);

  useEffect(() => {
    store.setState({ basket: basket.products });
  }, [basket]);

  useEffect(() => {
    store.setState({ productsStatus });
  }, [productsStatus]);

  useEffect(() => {
    store.setState({
      sum: buildBasketSum({
        totalUnit,
        totalUnitDiscounted,
        totalUnitExclVatDiscounted,
        totalDeposit,
        totalDepositExclVat,
        totalOrderDiscount,
        totalOrderDiscountable
      })
    });
  }, [
    totalUnit,
    totalUnitDiscounted,
    totalUnitExclVatDiscounted,
    totalOrderDiscount,
    totalOrderDiscountable,
    totalDeposit,
    totalDepositExclVat
  ]);

  useEffect(() => {
    store.setState({ allowances });
  }, [allowances]);

  useEffect(() => {
    store.setState({ userLimits });
  }, [userLimits]);

  return (
    <BasketContext.Provider value={store}>{children}</BasketContext.Provider>
  );
};

export const useBasketContext = <T,>(
  selector: (state: BasketContextState) => T
) => {
  const store = useContext(BasketContext);
  if (!store) {
    throw new Error("Missing BasketProvider");
  }
  return useStore(store, selector);
};
